import { getPublishedEssayByIdOptions } from "@/actions/essay/handler";
import { type EssayCategory, essayCategoryLabels } from "@/api/db/schema/essay";
import { trackContentEngagement } from "@/components/analytics/conversions";
import { Editor } from "@/components/blocks/editor-essay/editor";
import { LinkedProgramCard } from "@/components/essay/LinkedProgramCard";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { seo } from "@/utils/seo";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { ArrowLeft, ChevronRight, Tag } from "lucide-react";
import { useEffect } from "react";

export const Route = createFileRoute("/_app/essay/$essayId")({
  loader: async ({ context, params }) => {
    const data = await context.queryClient.ensureQueryData(
      getPublishedEssayByIdOptions(params.essayId),
    );
    if (data.status === 404) {
      throw notFound();
    }
    return data;
  },
  notFoundComponent: () => {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center pt-28 text-center">
        <h1 className="font-bold text-4xl">Essay Bulunamadı</h1>
        <p className="mt-4 text-muted-foreground">
          Aradığınız içerik mevcut değil, yayınlanmamış veya kaldırılmış
          olabilir.
        </p>
        <Button asChild className="mt-6">
          <Link to="/essay">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Tüm İçeriklere Geri Dön
          </Link>
        </Button>
      </div>
    );
  },
  component: EssayDetailPageComponent,
  head: ({ loaderData }) => {
    const essay = loaderData?.body as any;
    const categoryInfo =
      essayCategoryLabels[essay?.category as EssayCategory] ||
      essayCategoryLabels.general;
    const essayTags = Array.isArray(essay?.tags) ? essay.tags : [];

    return {
      meta: seo({
        title: essay?.title ? `${essay.title} | theCoach` : "İçerik Bulunamadı",
        description: essay?.metaDescription || essay?.excerpt || essay?.title,
        image: essay?.ogImageUrl || essay?.coverImageUrl || undefined,
        canonical: `https://atalaythecoach.com/essay/${essay?.essayId}`,
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: essay?.title,
            description: essay?.metaDescription || essay?.excerpt,
            articleSection: categoryInfo?.label,
            keywords: essayTags.length > 0 ? essayTags.join(", ") : undefined,
            image: [essay?.coverImageUrl, essay?.ogImageUrl].filter(Boolean),
            datePublished: essay?.publishedAt
              ? new Date(essay.publishedAt).toISOString()
              : undefined,
            dateModified: essay?.updatedAt
              ? new Date(essay.updatedAt).toISOString()
              : essay?.publishedAt
                ? new Date(essay.publishedAt).toISOString()
                : undefined,
            author: [
              {
                "@type": "Person",
                name: essay?.author?.userName || "Alican Atalay",
                url: "https://atalaythecoach.com",
              },
            ],
            publisher: {
              "@type": "Organization",
              name: "theCoach",
              logo: {
                "@type": "ImageObject",
                url: "https://atalaythecoach.com/favicon/favicon.svg",
              },
            },
          }),
        },
      ],
    };
  },
});

function EssayDetailPageComponent() {
  const { essayId } = Route.useParams();
  const essayQuery = useSuspenseQuery(getPublishedEssayByIdOptions(essayId));

  useEffect(() => {
    const timer = window.setTimeout(
      () => trackContentEngagement(essayId),
      30_000,
    );
    return () => window.clearTimeout(timer);
  }, [essayId]);

  const formatDate = (date: Date | null) => {
    if (!date) return "";
    return new Intl.DateTimeFormat("tr-TR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(date));
  };

  if (essayQuery.data.status === 404 || !essayQuery.data.body) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center pt-28 text-center">
        <h1 className="font-bold text-4xl">Essay Bulunamadı</h1>
        <p className="mt-4 text-muted-foreground">
          Aradığınız içerik mevcut değil, yayınlanmamış veya kaldırılmış
          olabilir.
        </p>
        <Button asChild className="mt-6">
          <Link to="/essay">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Tüm İçeriklere Geri Dön
          </Link>
        </Button>
      </div>
    );
  }

  const essay = essayQuery.data.body as any;
  const categoryInfo =
    essayCategoryLabels[essay.category as EssayCategory] ||
    essayCategoryLabels.general;
  const essayTags = Array.isArray(essay.tags) ? essay.tags : [];

  return (
    <div className="min-h-screen w-full bg-background pt-28 text-foreground sm:pt-32">
      <article className="container mx-auto max-w-2xl px-4 pb-20">
        {/* Breadcrumb Navigation */}
        <nav className="mb-6 flex flex-wrap items-center gap-1.5 text-muted-foreground text-xs">
          <Link to="/essay" className="transition-colors hover:text-foreground">
            İçerik ve Hesaplayıcılar
          </Link>
          <ChevronRight className="h-3 w-3" />
          <Link
            to="/essay"
            search={{ category: essay.category }}
            className="transition-colors hover:text-foreground"
          >
            {categoryInfo.label}
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span className="max-w-[200px] truncate font-medium text-foreground">
            {essay.title}
          </span>
        </nav>

        <header className="mb-8 sm:mb-10">
          {/* Category Badge & Tags at Top */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Link to="/essay" search={{ category: essay.category }}>
              <Badge
                variant="secondary"
                className="bg-primary/10 font-medium text-primary hover:bg-primary/20"
              >
                {categoryInfo.label}
              </Badge>
            </Link>
            {essayTags.map((tag: string) => (
              <Link key={tag} to="/essay" search={{ tag }}>
                <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-muted-foreground text-xs transition-colors hover:bg-primary/10 hover:text-primary">
                  <Tag className="mr-0.5 h-2.5 w-2.5" />#{tag}
                </span>
              </Link>
            ))}
          </div>

          <h1 className="font-extrabold font-serif text-3xl leading-tight tracking-tight sm:text-4xl md:text-5xl">
            {essay.title}
          </h1>

          <div className="mt-5 flex items-center gap-2 text-muted-foreground text-sm">
            <span>{formatDate(essay.publishedAt)}</span>
            {essay.readTime && (
              <>
                <span>·</span>
                <span>{essay.readTime}</span>
              </>
            )}
          </div>

          {/* Author Info */}
          {essay.author && (
            <div className="mt-6 flex items-center gap-3 border-t pt-5">
              <Avatar className="h-10 w-10">
                <AvatarImage
                  src={essay.author.image || ""}
                  alt={essay.author.userName}
                />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {essay.author.userName?.charAt(0).toUpperCase() || "A"}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium text-sm">{essay.author.userName}</p>
                <p className="text-muted-foreground text-xs">
                  Yazar & Antrenör
                </p>
              </div>
            </div>
          )}
        </header>

        {/* Cover Image */}
        {essay.coverImageUrl && (
          <div className="mb-10 overflow-hidden rounded-xl border">
            <img
              src={essay.coverImageUrl}
              alt={essay.title}
              className="h-auto w-full object-cover"
            />
          </div>
        )}

        {/* Lexical Content (Read-only) */}
        <div className="prose prose-neutral dark:prose-invert max-w-none">
          <Editor
            editorSerializedState={essay.content as any}
            readOnly={true}
          />
        </div>

        <LinkedProgramCard
          programId={essay.linkedProgramId}
          essayTitle={essay.title}
        />

        {/* Bottom Tags & Navigation Footer */}
        <footer className="mt-12 border-t pt-6">
          {essayTags.length > 0 && (
            <div className="mb-8">
              <h4 className="mb-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">
                İlgili Etiketler
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {essayTags.map((tag: string) => (
                  <Link key={tag} to="/essay" search={{ tag }}>
                    <Badge
                      variant="outline"
                      className="cursor-pointer transition-colors hover:border-primary hover:text-primary"
                    >
                      #{tag}
                    </Badge>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col items-center justify-between gap-4 rounded-xl border bg-muted/20 p-6 text-center sm:flex-row sm:text-left">
            <div>
              <h4 className="font-semibold text-base">
                Daha fazla içerik keşfedin
              </h4>
              <p className="text-muted-foreground text-xs">
                Hesaplayıcılar ve diğer {categoryInfo.label.toLowerCase()}{" "}
                yazılarına göz atın.
              </p>
            </div>
            <Button asChild size="sm">
              <Link to="/essay" search={{ category: essay.category }}>
                <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
                {categoryInfo.label} Yazıları
              </Link>
            </Button>
          </div>
        </footer>
      </article>
    </div>
  );
}
